import { useState } from 'react';
import {
    fetchMediaKit,
    MediaKitData,
    MediaKitSocials,
    saveMediaKitSocials,
} from '../services/mediaKitService';
import {
    mapSocialsToFormState,
    MediaKitSocialFormState,
} from '../utils/mediaKitFormUtils';
import {
    normalizeAtHandlesInSocials,
    validateFacebookName,
    validateHudlUrl,
    validateInstagramHandle,
    validateLinkedInUrl,
    validateTikTokHandle,
    validateXHandle,
} from '../utils/socialMediaUtils';
import MediaKitSocialFields from './MediaKitSocialFields';

interface MediaKitSocialEditProps {
    socials: MediaKitSocials;
    athleteUserId: number;
    onSaved: (data: MediaKitData) => void;
    onCancel: () => void;
}

export default function MediaKitSocialEdit({
    socials,
    athleteUserId,
    onSaved,
    onCancel,
}: MediaKitSocialEditProps) {
    const [socialDraft, setSocialDraft] = useState<MediaKitSocialFormState>(() => mapSocialsToFormState(socials));
    const [socialErrors, setSocialErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const validateSocialDraft = (): boolean => {
        const errors: Record<string, string> = {};
        const checks = [
            ['instagramHandle', validateInstagramHandle(socialDraft.instagramHandle)],
            ['tiktokHandle', validateTikTokHandle(socialDraft.tiktokHandle)],
            ['linkedinUrl', validateLinkedInUrl(socialDraft.linkedinUrl)],
            ['facebookName', validateFacebookName(socialDraft.facebookName)],
            ['hudlUrl', validateHudlUrl(socialDraft.hudlUrl)],
            ['xHandle', validateXHandle(socialDraft.xHandle)],
        ] as const;
        checks.forEach(([key, result]) => {
            if (!result.isValid && result.errorMessage) {
                errors[key] = result.errorMessage;
            }
        });
        setSocialErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const save = async () => {
        if (!validateSocialDraft()) {
            return;
        }
        setSaving(true);
        try {
            await saveMediaKitSocials(normalizeAtHandlesInSocials(socialDraft), athleteUserId);
            const updated = await fetchMediaKit(athleteUserId);
            onSaved(updated);
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        setSocialDraft(mapSocialsToFormState(socials));
        setSocialErrors({});
        onCancel();
    };

    return (
        <div className="media-kit-social-edit" data-testid="media-kit-social-edit">
            <MediaKitSocialFields
                values={socialDraft}
                errors={socialErrors}
                onChange={(key, value) => {
                    setSocialDraft((state) => ({ ...state, [key]: value }));
                    setSocialErrors((errors) => {
                        const next = { ...errors };
                        delete next[key];
                        return next;
                    });
                }}
            />
            <div className="media-kit-social-edit-actions">
                <button type="button" className="media-kit-btn-primary" onClick={save} disabled={saving}>
                    Save
                </button>
                <button type="button" className="media-kit-edit-cancel-btn" onClick={cancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
